/**
 * Editor del contenido de una plantilla de documento (TipTap).
 *
 * Un dato del trabajador o de la empresa es un nodo **atómico** («chip»): se
 * inserta desde «Insertar dato» y se borra entero, nunca letra por letra. Si se
 * pudiera editar por dentro, `{{trabajador.rut}}` terminaría como
 * `{{trabajador.ru}}` y el backend lo rechazaría al guardar sin que se viera por
 * qué.
 *
 * En el HTML queda como `<span data-variable="clave">{{clave}}</span>`: el
 * backend sustituye por el texto `{{clave}}` (el `span` es solo para que el
 * editor lo reconozca al volver a abrir la plantilla).
 */
import { useEffect, useRef, useState, type CSSProperties } from 'react'
import { EditorContent, Node, mergeAttributes, useEditor, type Editor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import { TableKit } from '@tiptap/extension-table'
import TextAlign from '@tiptap/extension-text-align'
import { Color, FontSize, TextStyle } from '@tiptap/extension-text-style'
import {
  AlignCenter, AlignJustify, AlignLeft, AlignRight, Bold, ImagePlus, Italic, List, ListOrdered,
  Minus, Redo2, Strikethrough, Table, Underline, Undo2,
} from 'lucide-react'
import type { VariableDePlantilla } from '../../hooks/usePlantillasDocumentos'
import { aChips } from './utilidades'
import { EstiloDeParrafo, Imagen, imagenComoDataUrl } from './extensiones'

const Variable = Node.create({
  name: 'variable',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      key: {
        default: null,
        parseHTML: (el: HTMLElement) => el.getAttribute('data-variable'),
        renderHTML: (attrs: { key?: string }) => ({ 'data-variable': attrs.key }),
      },
    }
  },
  parseHTML() {
    return [{ tag: 'span[data-variable]' }]
  },
  renderHTML({ node, HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes, { class: 'cl-variable' }), `{{${node.attrs.key}}}`]
  },
})

interface Props {
  value: string
  onChange: (html: string) => void
  variables: VariableDePlantilla[]
}

export function EditorTexto({ value, onChange, variables }: Props) {
  const editor = useEditor({
    extensions: [
      // Sin enlaces ni bloques de código: en un PDF impreso no sirven, y el
      // backend los quitaría al guardar.
      StarterKit.configure({ link: false, code: false, codeBlock: false }),
      TableKit.configure({ table: { resizable: false } }),
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TextStyle, FontSize, Color,
      EstiloDeParrafo,
      Imagen,
      Variable,
    ],
    content: aChips(value),
    // TipTap 3 no vuelve a dibujar con cada transacción: sin esto la barra solo
    // se actualizaba al cambiar el contenido, así que mover el cursor no marcaba
    // «Negrita» ni el interlineado, y seleccionar una imagen no mostraba sus
    // tamaños. La hoja es de unas páginas: dibujar en cada cambio no pesa.
    shouldRerenderOnTransaction: true,
    onUpdate: ({ editor: e }) => onChange(e.getHTML()),
    editorProps: { attributes: { 'aria-label': 'Contenido del documento' } },
  })

  // Al cambiar de plantilla (o al cargarla) se reemplaza el contenido; no en
  // cada tecla, que es cuando `value` sale del propio editor.
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(aChips(value), { emitUpdate: false })
    }
  }, [editor, value])

  if (!editor) return null
  return (
    <div className="rounded-xl border border-divider bg-surface overflow-hidden">
      <BarraDeHerramientas editor={editor} variables={variables} />
      <div className="bg-canvas p-3 sm:p-6 overflow-x-auto">
        {/* En oscuro la hoja se atenúa menos que en el visor de PDF: acá se
            trabaja sobre ella, y con el atenuado del visor (52 % de brillo) el
            blanco se veía gris amarillento y el texto perdía contraste. */}
        <div className="cl-paper cl-editor-documento mx-auto max-w-[820px] min-w-[320px]"
          style={{ '--paper-dim': 0.2 } as CSSProperties}>
          <EditorContent editor={editor} />
        </div>
      </div>
    </div>
  )
}

function Boton({ activo, onClick, titulo, children, deshabilitado }: {
  activo?: boolean; onClick: () => void; titulo: string; children: React.ReactNode; deshabilitado?: boolean
}) {
  return (
    <button type="button" onClick={onClick} title={titulo} aria-label={titulo} aria-pressed={activo}
      disabled={deshabilitado}
      className={`p-1.5 rounded-md text-content-muted hover:bg-soft hover:text-content disabled:opacity-40
        disabled:pointer-events-none ${activo ? 'bg-soft text-content' : ''}`}>
      {children}
    </button>
  )
}

function Separador() {
  return <span className="w-px h-5 bg-divider mx-1" aria-hidden="true" />
}

const INTERLINEADOS = [['1', 'Sencillo'], ['1.15', '1,15'], ['1.5', '1,5'], ['2', 'Doble']] as const
const ESPACIOS = [['0px', 'Sin espacio'], ['4px', 'Pequeño'], ['18px', 'Grande'], ['28px', 'Muy grande']] as const
const TAMANOS = ['9pt', '10pt', '12pt', '14pt', '16pt', '18pt', '24pt'] as const
const ANCHOS = ['25%', '50%', '75%', '100%'] as const

function BarraDeHerramientas({ editor, variables }: { editor: Editor; variables: VariableDePlantilla[] }) {
  const cadena = () => editor.chain().focus()
  const archivo = useRef<HTMLInputElement>(null)
  const [avisoImagen, setAvisoImagen] = useState('')
  const bloque = editor.isActive('heading') ? 'heading' : 'paragraph'
  const interlineado = (editor.getAttributes(bloque).interlineado as string | null) ?? ''
  const espacio = (editor.getAttributes(bloque).espacioDespues as string | null) ?? ''
  const tamano = (editor.getAttributes('textStyle').fontSize as string | null) ?? ''
  const color = (editor.getAttributes('textStyle').color as string | null) ?? '#1e293b'
  const imagen = editor.isActive('image') ? editor.getAttributes('image') : null

  const subirImagen = async (f: File | undefined) => {
    setAvisoImagen('')
    if (!f) return
    try {
      const src = await imagenComoDataUrl(f)
      cadena().insertarImagen({ src, alt: f.name }).run()
    } catch (e) {
      setAvisoImagen(e instanceof Error ? e.message : 'No se pudo insertar la imagen.')
    }
  }
  const estilo = editor.isActive('heading', { level: 1 }) ? 'h1'
    : editor.isActive('heading', { level: 2 }) ? 'h2'
      : editor.isActive('heading', { level: 3 }) ? 'h3' : 'p'
  const grupos = variables.reduce<Record<string, VariableDePlantilla[]>>((acc, v) => {
    (acc[v.group] ??= []).push(v)
    return acc
  }, {})
  const enTabla = editor.isActive('table')

  return (
    <div className="flex flex-wrap items-center gap-0.5 px-2 py-1.5 border-b border-divider bg-surface">
      <Boton titulo="Deshacer" onClick={() => cadena().undo().run()} deshabilitado={!editor.can().undo()}>
        <Undo2 size={15} /></Boton>
      <Boton titulo="Rehacer" onClick={() => cadena().redo().run()} deshabilitado={!editor.can().redo()}>
        <Redo2 size={15} /></Boton>
      <Separador />
      <select aria-label="Estilo del párrafo" value={estilo}
        onChange={(e) => {
          const v = e.target.value
          if (v === 'p') cadena().setParagraph().run()
          else cadena().toggleHeading({ level: Number(v[1]) as 1 | 2 | 3 }).run()
        }}
        className="cl-input h-8 py-0 text-sm w-auto">
        <option value="p">Normal</option>
        <option value="h1">Título 1</option>
        <option value="h2">Título 2</option>
        <option value="h3">Título 3</option>
      </select>
      <select aria-label="Tamaño de letra" title="Tamaño de letra" value={tamano}
        onChange={(e) => {
          const v = e.target.value
          if (v) cadena().setFontSize(v).run()
          else cadena().unsetFontSize().run()
        }}
        className="cl-input h-8 py-0 text-sm w-auto">
        <option value="">11 pt</option>
        {TAMANOS.map((t) => <option key={t} value={t}>{t.replace('pt', ' pt')}</option>)}
      </select>
      <label title="Color del texto"
        className="relative p-1.5 rounded-md hover:bg-soft cursor-pointer flex items-center">
        <span className="text-sm font-bold leading-none text-content" aria-hidden="true">A</span>
        <span className="absolute left-1.5 right-1.5 bottom-1 h-[3px] rounded-sm" style={{ background: color }}
          aria-hidden="true" />
        <input type="color" aria-label="Color del texto" value={color}
          onChange={(e) => cadena().setColor(e.target.value).run()}
          className="absolute inset-0 opacity-0 cursor-pointer" />
      </label>
      <Separador />
      <Boton titulo="Negrita" activo={editor.isActive('bold')} onClick={() => cadena().toggleBold().run()}>
        <Bold size={15} /></Boton>
      <Boton titulo="Cursiva" activo={editor.isActive('italic')} onClick={() => cadena().toggleItalic().run()}>
        <Italic size={15} /></Boton>
      <Boton titulo="Subrayado" activo={editor.isActive('underline')} onClick={() => cadena().toggleUnderline().run()}>
        <Underline size={15} /></Boton>
      <Boton titulo="Tachado" activo={editor.isActive('strike')} onClick={() => cadena().toggleStrike().run()}>
        <Strikethrough size={15} /></Boton>
      <Separador />
      {([['left', AlignLeft, 'Alinear a la izquierda'], ['center', AlignCenter, 'Centrar'],
        ['right', AlignRight, 'Alinear a la derecha'], ['justify', AlignJustify, 'Justificar']] as const)
        .map(([lado, Icono, titulo]) => (
          <Boton key={lado} titulo={titulo} activo={editor.isActive({ textAlign: lado })}
            onClick={() => cadena().setTextAlign(lado).run()}><Icono size={15} /></Boton>
        ))}
      <select aria-label="Interlineado" title="Interlineado" value={interlineado}
        onChange={(e) => cadena().setInterlineado(e.target.value || null).run()}
        className="cl-input h-8 py-0 text-sm w-auto">
        <option value="">Interlineado</option>
        {INTERLINEADOS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
      </select>
      <select aria-label="Espacio entre párrafos" title="Espacio entre párrafos" value={espacio}
        onChange={(e) => cadena().setEspacioDespues(e.target.value || null).run()}
        className="cl-input h-8 py-0 text-sm w-auto">
        <option value="">Espacio normal</option>
        {ESPACIOS.map(([v, t]) => <option key={v} value={v}>{t}</option>)}
      </select>
      <Separador />
      <Boton titulo="Lista con viñetas" activo={editor.isActive('bulletList')}
        onClick={() => cadena().toggleBulletList().run()}><List size={15} /></Boton>
      <Boton titulo="Lista numerada" activo={editor.isActive('orderedList')}
        onClick={() => cadena().toggleOrderedList().run()}><ListOrdered size={15} /></Boton>
      <Boton titulo="Línea horizontal" onClick={() => cadena().setHorizontalRule().run()}>
        <Minus size={15} /></Boton>
      <Separador />
      <Boton titulo="Subir imagen" onClick={() => archivo.current?.click()}>
        <ImagePlus size={15} /></Boton>
      <input ref={archivo} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
        onChange={(e) => { void subirImagen(e.target.files?.[0]); e.target.value = '' }} />
      {imagen && (
        <span className="flex items-center gap-1 text-xs">
          {ANCHOS.map((a) => (
            <button key={a} type="button" aria-pressed={imagen.ancho === a}
              className={`px-2 py-1 rounded-md hover:bg-soft text-content-muted ${imagen.ancho === a ? 'bg-soft text-content' : ''}`}
              onClick={() => cadena().updateAttributes('image', { ancho: a }).run()}>{a}</button>
          ))}
          <button type="button" className="px-2 py-1 rounded-md hover:bg-soft text-danger-content"
            onClick={() => cadena().deleteSelection().run()}>Quitar imagen</button>
        </span>
      )}
      {avisoImagen && <span role="alert" className="text-xs text-danger-content px-1">{avisoImagen}</span>}
      <Boton titulo="Insertar tabla" onClick={() => cadena().insertTable({ rows: 3, cols: 2, withHeaderRow: false }).run()}>
        <Table size={15} /></Boton>
      {enTabla && (
        <span className="flex items-center gap-1 text-xs">
          <button type="button" className="px-2 py-1 rounded-md hover:bg-soft text-content-muted"
            onClick={() => cadena().addRowAfter().run()}>+ Fila</button>
          <button type="button" className="px-2 py-1 rounded-md hover:bg-soft text-content-muted"
            onClick={() => cadena().addColumnAfter().run()}>+ Columna</button>
          <button type="button" className="px-2 py-1 rounded-md hover:bg-soft text-content-muted"
            onClick={() => cadena().deleteRow().run()}>− Fila</button>
          <button type="button" className="px-2 py-1 rounded-md hover:bg-soft text-content-muted"
            onClick={() => cadena().deleteColumn().run()}>− Columna</button>
          <button type="button" className="px-2 py-1 rounded-md hover:bg-soft text-danger-content"
            onClick={() => cadena().deleteTable().run()}>Quitar tabla</button>
        </span>
      )}
      <span className="flex-1" />
      <select aria-label="Insertar dato" value=""
        onChange={(e) => {
          const clave = e.target.value
          if (clave) cadena().insertContent({ type: 'variable', attrs: { key: clave } }).insertContent(' ').run()
        }}
        className="cl-input h-8 py-0 text-sm w-auto font-medium">
        <option value="">Insertar dato…</option>
        {Object.entries(grupos).map(([grupo, vs]) => (
          <optgroup key={grupo} label={grupo}>
            {vs.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
          </optgroup>
        ))}
      </select>
    </div>
  )
}
